import { Injectable } from '@angular/core';
import { Food, FoodHeader } from '@cf-shared/data-model/food';
import { CommonDbService } from 'services/common-db.service';
import { LoggingService } from 'services/logging.service';
import {
  PreparedStapleWords,
  StapleReadableDatabase,
  StapleSnapshotStore,
} from 'services/staple-db-snapshot';
import { StapleNames } from 'services/staple-suggestions';

/**
 * The device's own copy of the curated staple foods (food staples plan 4c, #5625).
 *
 * A thin shell. Every rule about WHEN the snapshot may be read and rebuilt lives in
 * `staple-db-snapshot.ts`, which is node-loadable and unit-tested — the client's unit tests are
 * plain node tests and cannot load an Angular service, so a rule kept inside this class could not be
 * tested at all. `CommonDbService` and `openCommonDbAsync` are the same split. This class supplies
 * the real database, the real clock and the real reporting, and nothing else.
 *
 * See `design-docs/food-staples.md`, "Plan 4c: staples on every device", under "Reading them on the
 * device".
 */
@Injectable()
export class StapleDbService {
  private store: StapleSnapshotStore;

  constructor(
    private commonDb: CommonDbService,
    private logging: LoggingService,
  ) {
    this.store = new StapleSnapshotStore({
      openCommonDbAsync: () => this.commonDb.initializeAsync(),
      // read through the service at CALL time, never captured once: `CommonDbService` replaces its
      // wrapper when an open had to build a fresh one, and re-connects the same one on a warm resume
      getDatabase: () => (this.commonDb.database as StapleReadableDatabase) ?? null,
      reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      nowMs: () => Date.now(),
    });
  }

  /** the staple rows the typing suggester matches against; empty while the snapshot is not ready */
  getStapleNamesAsync(): Promise<StapleNames[]> {
    return this.store.getStapleNamesAsync();
  }

  /** one staple document, or null when the snapshot is not ready or does not hold that id */
  getStapleByIdAsync(id: string): Promise<Food | null> {
    return this.store.getStapleByIdAsync(id);
  }

  /**
   * The name of the picture a staple shows, answered instantly from the snapshot already installed;
   * `undefined` while none is ready, or when that staple has no picture. It starts no read, because
   * templates call it while rendering — see the store's own comment.
   */
  pictureNameForId(foodId: string): string | undefined {
    return this.store.pictureNameForId(foodId);
  }

  /** every staple as a search header; empty while the snapshot is not ready */
  getAllStapleHeadersAsync(): Promise<FoodHeader[]> {
    return this.store.getAllStapleHeadersAsync();
  }

  /** the staples' names and aliases already split and stemmed, so an offline search does not redo it */
  getPreparedWordsAsync(): Promise<Map<string, PreparedStapleWords>> {
    return this.store.getPreparedWordsAsync();
  }

  /** build the snapshot if it is missing or stale; pages call this when they open */
  warmAsync(): Promise<boolean> {
    return this.store.warmAsync();
  }

  /** drop the snapshot, so one account's staples can never be read by the next */
  reset(): void {
    this.store.reset();
  }
}
